import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";

const BLOCKS = [
  { t: "img", src: "/media/companies-concept.jpg", alt: "Concept for a large workplace piece — a driftwood arch with engraved name tags, fern stones, feathers and brass details in a warm lobby", caption: "An example of what can be done — a large work for a workplace (concept)." },
  { t: "p", x: "Some things belong to one person.\nOthers belong to a whole group of people." },
  { t: "p", x: "The idea for Collective Pieces grew from the very first piece we created together — made around a mother-to-be, with each person bringing something of their own into the work.\nA fragment.\nA memory.\nA few words.\nA material with meaning.\nA wish for what comes next." },
  { t: "p", x: "That same idea can live within a workplace.\nI invite teams, companies and communities to create a larger, one-of-a-kind piece together. Each person contributes something — tangible or intangible — and through the process, those individual fragments become part of one shared object." },
  { t: "p", x: "The finished piece might mark an anniversary, a transition, a new beginning, the end of a chapter, or simply the people who happened to share this particular moment in time." },
  { t: "p", x: "It can be created from personal objects, natural materials, written words, photographs, pieces from the workplace or other fragments carrying a story." },
  { t: "pull", x: "It is not about making something perfect.\nIt is about making something that belongs to you." },
  { t: "kicker", x: "The process" },
  { t: "p", x: "Every collaboration begins with a conversation.\nTogether, we explore what you want the piece to hold — the people, the story, the place or the moment behind it. From there, I develop the framework for the work and guide the group through the process of contributing to it." },
  { t: "p", x: "Depending on the project, we can create together in the atelier, at your workplace, or through a combination of individual contributions and my work in the studio.\nThe result is a piece that could never be recreated in exactly the same way.\nBecause it carries the traces of the people who made it." },
  { t: "pull", x: "A collective memory, made visible." },
];

export default function CompaniesPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Collective Pieces — The Things We Keep";
    return () => {
      document.title = "The Things We Keep — Objects with a story";
    };
  }, []);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="companies-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="ttwk-label" data-testid="companies-page-eyebrow">
            For companies
          </p>
          <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-7xl" data-testid="companies-page-title">
            Collective Pieces
          </h1>
          <p className="mt-6 max-w-xl font-serif text-2xl font-light italic text-terracotta sm:text-3xl">
            Something made together. Something worth keeping.
          </p>
        </Reveal>

        <div className="mt-16 max-w-2xl">
          {BLOCKS.map((b, i) => {
            if (b.t === "img") {
              return (
                <Reveal key={i}>
                  <div className="img-frame my-16 overflow-hidden rounded-sm border border-clay/60">
                    <img src={b.src} alt={b.alt} className="w-full object-cover" data-testid={`companies-image-${i}`} />
                  </div>
                  <p className="-mt-10 font-serif text-sm italic text-umber/60">{b.caption}</p>
                </Reveal>
              );
            }
            if (b.t === "kicker") {
              return (
                <Reveal key={i}>
                  <p className="mt-16 border-t border-clay/60 pt-10 text-[11px] uppercase tracking-[0.25em] text-terracotta" data-testid="companies-process-kicker">
                    {b.x}
                  </p>
                </Reveal>
              );
            }
            if (b.t === "pull") {
              return (
                <Reveal key={i}>
                  <p className="my-12 font-serif text-3xl font-light italic leading-snug text-terracotta sm:text-4xl">
                    {b.x.split("\n").map((line, j) => (
                      <span key={j}>
                        {j > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </p>
                </Reveal>
              );
            }
            return (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.15)}>
                <p className="mt-8 font-serif text-xl font-light leading-relaxed text-umber first:mt-0 sm:text-2xl">
                  {b.x.split("\n").map((line, j) => (
                    <span key={j}>
                      {j > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
            className="mt-16 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="companies-page-cta"
          >
            Start a conversation
          </button>
        </Reveal>
      </div>
    </div>
  );
}
